import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import dayjs from 'dayjs';
import type { FC, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { Button } from 'src/common/components/button';
import { message } from 'src/common/components/message';
import type { ModalSchema } from 'src/common/components/next-modal';
import { useOpenModal } from 'src/common/components/next-modal';
import { DATE_TIME_FORMAT } from 'src/common/const';
import { request } from 'src/common/request';
import { fetchSpaces } from 'src/hooks/space';
import { Images } from 'src/image';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { confetti } from 'src/utils/confetti';
import { openQiMoor } from 'src/utils/qi-moor';

// #region 对公转账
export const PayOfflineModal: FC<{
  orderId: string;
  price: number;
  endTime: number;
  remark?: string;
  changePayType?: () => void;
  customFooter?: ReactNode;
  onCloseModal: ModalSchema.CloseModalType;
  onFinish?: () => void;
}> = (props) => {
  const { t } = useAppTranslation();
  const { price, endTime, remark, changePayType, customFooter, onCloseModal, onFinish } = props;
  const intervalTimer = useRef<NodeJS.Timeout>();
  const openModal = useOpenModal();
  /** 如果用户网速慢的话，可能会触发多次刷新订单，这里加个flag */
  const refreshRunning = useRef<boolean>(false);

  // 刷新订单
  const refreshOrder = (orderId: string) => {
    if (orderId) {
      refreshRunning.current = true;
      void request.infra.getOrderStatus.raw(orderId).then(async (res) => {
        if (res.code === 200) {
          switch (res.data.status) {
            // 已经支付
            case 20:
              intervalTimer.current && clearInterval(intervalTimer.current);
              await fetchSpaces();
              onCloseModal();
              void confetti.fireworks();
              onFinish?.();
              message.success(t('common.payment_success'));
              break;
            // 已经取消
            case 30:
              message.error(t('order.order_invalid'));
              intervalTimer.current && clearInterval(intervalTimer.current);
              break;
            // 待支付
            case 10:
              refreshRunning.current = false;
              break;
            default:
          }
        } else {
          intervalTimer.current && clearInterval(intervalTimer.current);
        }
      });
    } else {
      onCloseModal();
    }
  };

  const onCopy = () => {
    void writeTextInClipboard(
      `${t('order.transfer_amount')}：${price}\n${t('order.payee_name')}：${t(
        'order.company_name'
      )}\n${t('order.payee_account')}：110944870510101\n${t('order.bank')}：${t(
        'order.bank_name'
      )}\n${t('order.transfer_remark')}：${remark}`
    );
  };

  useEffect(() => {
    intervalTimer.current = setInterval(() => {
      if (!refreshRunning.current) {
        void refreshOrder(props.orderId);
      }
    }, 3000);
    return () => {
      intervalTimer.current && clearInterval(intervalTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.orderId]);

  return (
    <div className="w-[550px] px-12 pt-6 pb-10 text-center">
      <div className="text-h2 mb-6">{t('order.buy_team')}</div>
      <div className="mb-2.5 text-t2-medium">
        {t('order.transfer_before')}
        <span className="text-blue">
          {dayjs(endTime).format(DATE_TIME_FORMAT)}
          {t('order.before')}
        </span>
        {t('order.transfer_notice')}
      </div>
      <div className="text-t4 text-grey3 mb-7">
        {t('order.any_question')}
        <span
          className="border-b border-grey3 cursor-pointer"
          onClick={() => {
            openQiMoor(openModal);
          }}
        >
          {t('common.online_service')}
        </span>
        {t('order.help_solve')}
      </div>
      <div className="border border-black/10 rounded py-4 px-5 mb-2.5 relative">
        <div className="flex items-center h-10">
          <Button onClick={onCopy}>{t('order.copy_info')}</Button>
        </div>
        {[
          {
            title: t('order.transfer_amount'),
            content: price,
          },
          {
            title: t('order.payee_name'),
            content: t('order.company_name'),
          },
          {
            title: t('order.payee_account'),
            content: '110944870510101',
          },
          {
            title: t('order.bank'),
            content: t('order.bank_name'),
          },
          {
            title: t('order.transfer_remark'),
            content: remark,
          },
        ].map((item) => (
          <div
            key={item.title}
            className="flex items-center justify-between h-10 text-t2 text-grey3"
          >
            <span>{item.title}</span>
            <span className="text-t2-medium text-black">{item.content}</span>
          </div>
        ))}
        <img src={Images.orderRemarks} className="absolute -bottom-6 right-3 pointer-events-none" />
      </div>
      <div className="text-red text-t2 text-right pr-20">{t('order.remark_warning')}</div>
      <div className="flex items-center justify-center my-4">{customFooter}</div>
    </div>
  );
};
// #endregion
